/**
 * Timeline Carousel - Performance Optimized
 * Features:
 * - IntersectionObserver: Animation only when visible
 * - Passive event listeners for better scroll performance
 * - Two-row horizontal scroll (opposite directions)
 * - Click to pause and show detail overlay
 */
class TimelineCarousel {
  constructor() {
    this.timeline = document.querySelector('[data-timeline]');
    this.cards = document.querySelectorAll('[data-timeline-card]');
    this.rows = document.querySelectorAll('[data-timeline-row]');
    this.overlay = document.querySelector('[data-timeline-overlay]');
    this.selectedCard = null;
    this.isPaused = false;

    if (!this.timeline || !this.cards.length) return;
    this.init();
  }

  init() {
    // Add click listeners to all timeline cards (passive for better scroll performance)
    this.cards.forEach((card) => {
      card.addEventListener('click', () => this.handleCardClick(card), { passive: true });
    });

    // Add click listeners to close buttons and backdrop
    document.querySelectorAll('[data-timeline-close]').forEach((btn) => {
      btn.addEventListener('click', () => this.closeOverlay(), { passive: true });
    });

    // Close overlay on Escape key (passive)
    document.addEventListener('keydown', (e) => {
      if (e.key === 'Escape' && !this.overlay.hidden) {
        this.closeOverlay();
      }
    }, { passive: true });

    // Observe visibility for performance optimization
    this.observeVisibility();
  }

  observeVisibility() {
    // Only animate when timeline is visible in viewport
    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          this.rows.forEach(row => row.classList.add('is-visible'));
        } else {
          this.rows.forEach(row => row.classList.remove('is-visible'));
        }
      });
    }, { threshold: 0.1 });

    observer.observe(this.timeline);
  }

  handleCardClick(card) {
    // If same card clicked, close overlay
    if (this.selectedCard === card) {
      this.closeOverlay();
      return;
    }

    // Pause both rows
    this.selectedCard = card;
    this.isPaused = true;
    this.rows.forEach((row) => row.classList.add('is-paused'));

    // Update selected state
    this.cards.forEach((c) => c.classList.remove('is-selected'));
    card.classList.add('is-selected');

    // Update overlay content from data attributes
    const yearEl = this.overlay.querySelector('[data-timeline-year]');
    const titleEl = this.overlay.querySelector('[data-timeline-title]');
    const descriptionEl = this.overlay.querySelector('[data-timeline-description]');

    if (yearEl) yearEl.textContent = card.dataset.year || '';
    if (titleEl) titleEl.textContent = card.dataset.title || '';
    if (descriptionEl) descriptionEl.textContent = card.dataset.description || '';

    // Show overlay
    this.overlay.hidden = false;
  }

  closeOverlay() {
    // Hide overlay
    this.overlay.hidden = true;

    // Remove selected state from card
    if (this.selectedCard) {
      this.selectedCard.classList.remove('is-selected');
    }
    this.selectedCard = null;

    // Resume rows
    this.isPaused = false;
    this.rows.forEach((row) => row.classList.remove('is-paused'));
  }
}

// Initialize on DOM ready
document.addEventListener('DOMContentLoaded', () => new TimelineCarousel());

// Re-initialize for Shopify theme editor
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', (e) => {
    if (e.target.querySelector('[data-timeline]')) {
      new TimelineCarousel();
    }
  });
}
